/**
 * Componente Scanner — modal com câmera lendo barcode.
 *
 * Estratégia:
 *  1. Tenta a Web BarcodeDetector API (padrão W3C, nativa no Edge/Chromium 88+).
 *  2. Fallback: @zxing/browser (MIT) cobre Firefox/Safari/Edge antigos.
 *
 * Formatos lidos: EAN-13 (caixa fabricante) e Data Matrix / QR (etiqueta interna).
 */
import { useEffect, useRef, useState } from "react";

type Formato = "ean_13" | "data_matrix" | "qr_code" | "code_128";
const FORMATOS_DEFAULT: Formato[] = ["ean_13", "data_matrix", "qr_code", "code_128"];

interface Props {
  open: boolean;
  onScan: (codigo: string) => void;
  onClose: () => void;
  formats?: Formato[];
}

// BarcodeDetector type guard — alguns browsers ainda não declaram no lib.dom
type BarcodeDetectorCtor = new (opts: { formats: string[] }) => {
  detect: (source: HTMLVideoElement | ImageBitmap) => Promise<Array<{ rawValue: string; format: string }>>;
};
function getBarcodeDetector(): BarcodeDetectorCtor | null {
  const w = window as unknown as { BarcodeDetector?: BarcodeDetectorCtor };
  return w.BarcodeDetector ?? null;
}

export function Scanner({ open, onScan, onClose, formats = FORMATOS_DEFAULT }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [motor, setMotor] = useState<"nativo" | "zxing" | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let stream: MediaStream | null = null;
    let rafId = 0;
    let zxingControls: { stop: () => void } | null = null;

    async function start() {
      setErro(null);
      const video = videoRef.current;
      if (!video) return;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        video.srcObject = stream;
        await video.play();

        const Detector = getBarcodeDetector();
        if (Detector) {
          setMotor("nativo");
          const det = new Detector({ formats });
          const loop = async () => {
            if (cancelled) return;
            try {
              const found = await det.detect(video);
              if (found.length > 0) {
                onScan(found[0].rawValue);
                return;
              }
            } catch {
              // ignora erro pontual; segue tentando
            }
            rafId = requestAnimationFrame(loop);
          };
          rafId = requestAnimationFrame(loop);
        } else {
          // Fallback zxing
          setMotor("zxing");
          const { BrowserMultiFormatReader } = await import("@zxing/browser");
          const reader = new BrowserMultiFormatReader();
          zxingControls = await reader.decodeFromVideoElement(video, (result) => {
            if (result) {
              onScan(result.getText());
            }
          });
        }
      } catch (e) {
        const err = e as Error;
        setErro(
          err.name === "NotAllowedError"
            ? "Permissão de câmera negada. Conceda acesso e tente de novo."
            : err.name === "NotFoundError"
              ? "Nenhuma câmera disponível neste dispositivo."
              : `Erro ao abrir câmera: ${err.message}`,
        );
      }
    }

    start();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (zxingControls) zxingControls.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [open, formats, onScan]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-display text-lg font-semibold">Scanner</h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>
        <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            muted
            playsInline
          />
          {/* Mira / moldura de enquadramento — ajuda a apontar o código no celular */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              className="relative h-[42%] w-[78%]"
              style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }}
            >
              {/* cantos */}
              <span className="absolute -left-0.5 -top-0.5 h-7 w-7 rounded-tl-md border-l-4 border-t-4 border-white" />
              <span className="absolute -right-0.5 -top-0.5 h-7 w-7 rounded-tr-md border-r-4 border-t-4 border-white" />
              <span className="absolute -bottom-0.5 -left-0.5 h-7 w-7 rounded-bl-md border-b-4 border-l-4 border-white" />
              <span className="absolute -bottom-0.5 -right-0.5 h-7 w-7 rounded-br-md border-b-4 border-r-4 border-white" />
              {/* linha de leitura */}
              <span className="scanline absolute inset-x-1" />
            </div>
          </div>
        </div>
        <style>{`
          @keyframes medsupply-scan { 0% { top: 6%; } 50% { top: 90%; } 100% { top: 6%; } }
          .scanline {
            height: 2px;
            background: #B40B24;
            box-shadow: 0 0 8px 1px rgba(180,11,36,0.9);
            animation: medsupply-scan 2s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) { .scanline { animation: none; top: 50%; } }
        `}</style>
        <div className="mt-2 flex items-center justify-between text-xs text-ink-muted">
          <span>Encaixe o código de barras dentro da moldura</span>
          {motor ? <span className="badge bg-gray-100">motor: {motor}</span> : null}
        </div>
        {erro ? <p className="mt-2 text-sm text-anarosa">⚠️ {erro}</p> : null}
      </div>
    </div>
  );
}
